import { ReactElement, memo, FC, useEffect, useMemo, useState } from 'react'
import { Observable, Subscription } from 'rxjs'
import { createAudioCtx, getVolumeLevelObservable } from 'components/features/coaching/advisorVideoCall/helpersMicCheck'

export type AudioStreamVolumeIndicatorProps = {
  audioStreamTrack: MediaStreamTrack,
  renderVolumeValueProp (volumeValueOrNull: number | null, error: Error | null): ReactElement | null,
}

export const AudioStreamVolumeIndicatorHandler: FC<AudioStreamVolumeIndicatorProps> = memo(({
  audioStreamTrack,
  renderVolumeValueProp,
}) => {
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null)
  const [audioStreamVolumeObservable, setAudioStreamVolumeObservable] = useState<Observable<number> | null>(null)
  const [volumeValueDb, setVolumeValueDb] = useState<number | null>(null)
  const [errorWithinObservable, setErrorWithinObservable] = useState<Error | null>(null)
  const createObservableForMediaStream = useMemo<((mediaStream: MediaStream) => Observable<number>)| null>(() => {
    if (!audioContext) {
      return null
    }
    return (mediaStream: MediaStream) => getVolumeLevelObservable(audioContext, mediaStream)
  }, [audioContext])

  useEffect(() => {
    const audioCtx = createAudioCtx()
    if (!audioCtx) {
      return
    }

    setAudioContext(audioCtx)
    return () => {
      audioCtx.close()
      setAudioContext(null)
    }
  }, [])
  useEffect((): (() => void) | void => {
    if (!audioContext || !audioStreamTrack || !createObservableForMediaStream) {
      return
    }

    const mediaStream = new MediaStream()
    mediaStream.addTrack(audioStreamTrack)

    let observable: Observable<number>

    try {
      observable = createObservableForMediaStream(mediaStream)
    } catch (error) {
      setErrorWithinObservable(error)
      return
    }
    setAudioStreamVolumeObservable(observable)

    return () => {
      setAudioStreamVolumeObservable(null)
      mediaStream.removeTrack(audioStreamTrack)
    }
  }, [audioContext, audioStreamTrack, createObservableForMediaStream])
  useEffect((): (() => void) | void => {
    if (!audioStreamVolumeObservable) {
      return
    }

    // Not sure why ESlint is not seeing this be re-assigned. might be a bug
    // eslint-disable-next-line prefer-const
    let subscription: Subscription

    function handleVolumeValue (volumeValueInDb: number): void {
      setVolumeValueDb(volumeValueInDb)
    }
    function unsubscribe (): void {
      subscription.unsubscribe()
    }
    function handleError (error: Error): void {
      setErrorWithinObservable(error)
      unsubscribe()
    }
    subscription = audioStreamVolumeObservable.subscribe({
      next: handleVolumeValue,
      error: handleError,
      complete: unsubscribe,
    })
    return unsubscribe
  }, [audioStreamVolumeObservable])
  return renderVolumeValueProp(volumeValueDb, errorWithinObservable)
})
